import { useMemo, useState, useEffect, useContext } from 'react';
import NewApplication from './components/ApplicantHome/NewApplication';
import ApplicantHome from './components/ApplicantHome/ApplicantHome';
import UAELogin from './components/UAEPass/UAELogin';
import { useIntl } from 'react-intl';
import messages from './resources';
import { spacing80, spacing40, spacing90, fountain100, colorBackgroundDefault } from '@ellucian/react-design-system/core/styles/tokens';
import { makeStyles, useMediaQuery, Alert, INLINE_VARIANT } from '@ellucian/react-design-system/core';
import { useTheme } from '@mui/material';
import { useApi } from './components/ApiContext';
import { routes, CONSTANTS } from './components/Helpers/Configuration';
import Application from './components/ApplicationFormLayout/Application';
import { createEDSTheme } from '@ellucian/react-design-system/core/themes';
import parseTheme from './utilities/parseTheme';
import GlobalLocaleContext from './components/GlobalLocale/GlobalLocaleContext';
import isInIframe from './utilities/isInIframe';
import getQueryParam from './utilities/getQueryParam';
import rtlDetect from 'rtl-detect';
import { getDisplayForLocale } from './components/Helpers/messagesHelpers';
import MFAWrapper from './components/MFAAuth/MFAWrapper';
import { BrowserRouter as Router, Route, useLocation, matchPath } from 'react-router-dom';
import { IconSprite } from '@ellucian/ds-icons/lib';
import RetrievalForm from './components/RetrievalForm/RetrievalForm';
import RetrievalSubmitted from './components/RetrievalForm/RetrievalSubmitted';
import { IntlProvider } from 'react-intl';
import UserContext from './components/UserContext';
import Header from './components/Header/Header';
import { ApiProvider } from './components/ApiContext';
import Timeout from './components/Timeout/Timeout';
import { SnackbarProvider } from './components/SnackbarContext/SnackbarContext';
import RootContext from './components/RootContext/RootContext';
import InvalidApplicantLink from './components/InvalidApplicantLink/invalidApplicantLink';
import ExternalApplication from './components/ApplicationFormLayout/ExternalApplication/ExternalApplication';
import InvalidShareUrl from './components/InvalidShareUrl/InvalidShareUrl';
import LanguageDropdown from './components/LanguageDropdown/LanguageDropdown';
import Footer from './components/Footer';
import EDSContent from './EDSContent';
import { VirtualAdvisorProvider } from './virtual-advisor/VirtualAdvisorProvider';
import { VirtualAdvisorWidget } from './virtual-advisor/VirtualAdvisorWidget';

const useStyles = makeStyles({
    name: 'appapplicant'
})(theme => ({
    content: {
        margin: `${spacing80} auto 0 auto`,
        maxWidth: '1280px',
        '&.embedded': {
            marginTop: 0,
        }
    },
    inquiryContent: {
        margin: `${spacing80} auto 0 auto`,
        maxWidth: '800px',
    },
    copyright: {
        textAlign: 'center',
        marginBottom: spacing40,
    },
    root: {
        [theme.breakpoints.down('md')]: {
            marginBottom: spacing90,
        },
    },
    languageDropdownContainer: {
        display: 'flex',
    },
    languageDropdownRoot: {
        marginLeft: 'auto',
    },
    overlayDisable: {
        backgroundColor: fountain100,
        position: 'fixed',
        width: '100%',
        height: '100%',
        zIndex: '1000',
        top: '0px',
        left: '0px',
        opacity: '0.5',
    },
    overlay: {
        backgroundColor: colorBackgroundDefault,
        position: 'fixed',
        width: '100%',
        height: '100%',
        zIndex: '1000',
        top: '0px',
        left: '0px',
    },
    loadingIndicator: {
        position: 'fixed',
        left: '50%',
        top: '50%',
    },
    connectivityBanner: {
        position: 'sticky',
        top: 0,
        zIndex: 1300,
        width: '100%',
        [theme.breakpoints.up('md')]: {
            top: '4rem',
        }
    }
}));
const userCtxValue = {
    principle: window.PRELOADED_STATE?.user?.name,
    signOut: () => {
        window.location = '/logout';
    },
};
const baseTheme = createEDSTheme('light');

const connectivityBannerRoutes = [
    routes.applicationForm,
    routes.applicationFormPageById,
    routes.applicationFormReviewPage,
    routes.applicationFormStart,
    routes.applicationFormSubmitPage,
    routes.applicationFormWithdrawnPage,
    routes.applicationFormSupplementalItemsPage,
    routes.applicationFormSupportingDocumentsPage,
    routes.applicationFormPayments,
    routes.applicationFormSupplementalForm,
    routes.externalApplication,
    routes.externalApplicationPageById,
    routes.externalApplicationSupplementalItemsPage,
    routes.externalApplicationSupportingDocumentsPage,
    routes.externalApplicationPayments,
    routes.externalApplicationwReviewPage,
    routes.applicationShare,
];

const ConnectivityBanner = ({ showConnectivityBanner, classes, formatMessage }) => {
    const location = useLocation();
    const shouldShowOnCurrentRoute = useMemo(() => {
        return Boolean(matchPath(location.pathname, { path: connectivityBannerRoutes }));
    }, [location.pathname]);

    if (!showConnectivityBanner || !shouldShowOnCurrentRoute) {
        return null;
    }

    return (
        <div className={classes.connectivityBanner}>
            <Alert
                alertType="warning"
                open={true}
                userDismissable={false}
                variant={INLINE_VARIANT}
                text={formatMessage({
                    id: 'connectivityWarningBanner'
                })}
            />
        </div>
    );
};

const AppContent = () => {
    const { classes, cx } = useStyles();
    const api = useApi();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    //timeout handlers, force a timeout when the user had idle timed out and reset the timeout afterwards.
    const [isTimedout, setTimeout] = useState(false);
    const [isOffline, setIsOffline] = useState(typeof navigator !== 'undefined' ? !navigator.onLine : false);
    const [hasConnectionFailure, setHasConnectionFailure] = useState(false);
    const { isThemeLoading } = useContext(RootContext);

    const showConnectivityBanner = isOffline || hasConnectionFailure;
    const forceTimeout = async () => {
        await api.applicantLogout();
        if (isTimedout != true) {
            setTimeout(true);
        }
    };
    const handleReset = () => {
        if (isTimedout) {
            setTimeout(false);
        }
    };
    const isEmbedded = useMemo(() => isInIframe(), []);
    const { globalLocale, updateDisplayLocale } = useContext(GlobalLocaleContext);
    const { formatMessage } = useIntl();

    useEffect(() => {
        updateDisplayLocale(getDisplayForLocale(globalLocale, formatMessage));
    }, [globalLocale, formatMessage]);

    useEffect(() => {
        const handleOffline = () => {
            setIsOffline(true);
            setHasConnectionFailure(true);
        };

        const handleOnline = () => {
            setIsOffline(false);
            setHasConnectionFailure(false);
        };

        const handleConnectionFailure = () => {
            setHasConnectionFailure(true);
        };

        window.addEventListener('offline', handleOffline);
        window.addEventListener('online', handleOnline);
        window.addEventListener('applicant-connectivity-issue', handleConnectionFailure);

        return () => {
            window.removeEventListener('offline', handleOffline);
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('applicant-connectivity-issue', handleConnectionFailure);
        };
    }, []);

    const contentClasses = useMemo(() => {
        return cx(classes.content, {
            embedded: isEmbedded || isMobile,
        });
    }, [classes, isEmbedded, isMobile]);

    const showLanguageDropDown = useMemo(() => isEmbedded, [isEmbedded]);

    // Virtual Advisor config comes from saved global settings in preloaded state.
    const vaConfig = useMemo(() => {
        const ps = window.PRELOADED_STATE?.virtualAdvisor || {};
        const tenantSubdomain = ps.tenantSubdomain?.trim?.() || '';

        return {
            enabled: Boolean(ps.enabled) && Boolean(tenantSubdomain),
            tenantId: ps.tenantId || '',
            tenantSubdomain,
            baseHost: ps.baseHost || 'studentadvisor.ai',
            categoryGroupId: ps.categoryGroupId,
            brandColor: ps.brandColor || CONSTANTS.virtualAdvisor.defaultColor,
            launcherIconUrl: ps.launcherIconUrl || '',
        };
    }, []);

    return (
        <ApiProvider>
            <UserContext.Provider value={userCtxValue}>
                <IconSprite />
                <Router>
                    <Timeout forceTimeout={forceTimeout} isTimedout={isTimedout} handleReset={handleReset}/>
                    <div className={classes.root}>
                        <ConnectivityBanner
                            showConnectivityBanner={showConnectivityBanner}
                            classes={classes}
                            formatMessage={formatMessage}
                        />
                        {!isEmbedded && !isThemeLoading &&
                            <Header showUtilityBar={false} />
                        }
                        {showLanguageDropDown && !isThemeLoading &&
                            <div className={classes.languageDropdownContainer}>
                                <LanguageDropdown classes={{ root: classes.languageDropdownRoot }} />
                            </div>
                        }
                        <div className={contentClasses}>
                            <Route exact path={routes.home} component={ApplicantHome}/>
                            <Route exact path={routes.newApplication} component={NewApplication}/>
                            <Route exact path={routes.uaeLogin} component={UAELogin}/>
                        </div>
                        <Route
                            exact
                            path={routes.invalidShareUrl}
                            render={(props) => <InvalidShareUrl {...props} />}
                        />
                        <Route path={routes.externalApplication} render={ (props) => <ExternalApplication {...props} isTimedout={isTimedout} /> } />
                        <Route
                            exact
                            path={routes.retrieval}
                        >
                            <RetrievalForm isTimedout={isTimedout}/>
                        </Route>
                        <Route exact path={routes.retrievalSubmitted}>
                            <RetrievalSubmitted commType={CONSTANTS.communicationTypes.email} />
                        </Route>
                        <Route path={routes.applicationFormSupplementalForm}>
                            <Application />
                        </Route>
                        <Route
                            exact
                            path={routes.invalidApplicantLink}
                            render={(props) => <InvalidApplicantLink {...props} />}
                        />
                        <Route
                            path={routes.applicationForm}
                            component={Application}
                        />
                        <Route
                            path={routes.totp}
                            component={MFAWrapper}
                        />
                        {!isEmbedded && !isThemeLoading && <Footer />}
                        {!isEmbedded && !isThemeLoading && vaConfig.enabled && (
                            <VirtualAdvisorProvider
                                enabled={vaConfig.enabled}
                                tenantSubdomain={vaConfig.tenantSubdomain}
                                baseHost={vaConfig.baseHost}
                                categoryGroupId={vaConfig.categoryGroupId}
                                showForUser={() => !isEmbedded}
                                zIndex={CONSTANTS.virtualAdvisor.zIndex}
                            >
                                <VirtualAdvisorWidget
                                    launcherColor={vaConfig.brandColor}
                                    launcherIconUrl={vaConfig.launcherIconUrl}
                                    currentUser={userCtxValue}
                                    currentRoute={window.location.pathname}
                                />
                            </VirtualAdvisorProvider>
                        )}
                    </div>
                </Router>

            </UserContext.Provider>
        </ApiProvider>
    );
};

// Swap message keys
(() => {
    const settingMessages = window.PRELOADED_STATE?.globalSettings?.settingMessages;
    let settings = window.PRELOADED_STATE?.globalSettings?.generalSettings;

    if (!settingMessages || !settingMessages.languages) {
        throw new Error('Unable to read setting messages from preloaded state');
    }
    else if (!settings) {
        throw new Error('Unable to read general settings from preloaded state');
    }

    if (window.PRELOADED_STATE?.globalSettings?.applicationRetrievalSettings) {
        settings = settings.concat(
            Object.values(window.PRELOADED_STATE.globalSettings.applicationRetrievalSettings)
        );
    }

    const locales = Object.keys(settingMessages.languages);
    let newMessages;
    locales.forEach((locale) => {
        newMessages = window.PRELOADED_STATE.globalSettings.settingMessages.languages[locale]?.messages;
        settings.map((setting) => {
            if (setting.localized) {
                newMessages[setting.settingName] = newMessages[setting.settingValue];
                delete newMessages[setting.settingValue];
            }
            window.PRELOADED_STATE.globalSettings.settingMessages.languages[locale].messages = newMessages;
        });
    });
})();

export const initialLocale = (() => {
    const settingMessages = window.PRELOADED_STATE.globalSettings.settingMessages;
    const locales = Object.keys(settingMessages.languages);
    const disabledLanguages = window.PRELOADED_STATE.globalSettings.disabledLanguages || [];
    let selectedLocale;
    const queryParamLocale = getQueryParam('localeCode');
    if (queryParamLocale && locales.includes(queryParamLocale)) {
        // A disabled locale passed via URL falls back to the default language.
        selectedLocale = disabledLanguages.includes(queryParamLocale)
            ? window.PRELOADED_STATE.globalSettings.defaultLanguage
            : queryParamLocale;
    } else {
        const { languages } = navigator; // Gets browser languages
        for (const browserLocale of languages) {
            const browserLang = browserLocale.split('-')[0];
            selectedLocale = locales.includes(browserLocale) && browserLocale;
            selectedLocale = selectedLocale || (locales.includes(browserLang) && browserLang);
            selectedLocale = selectedLocale || locales.find(x => x.split('-')[0] === browserLang);
            if (selectedLocale) {
                // If this matched browser locale is disabled, we fallback to use defaultLanguage.
                if (disabledLanguages.includes(selectedLocale)) {
                    // disable only that exact locale
                    selectedLocale = window.PRELOADED_STATE.globalSettings.defaultLanguage;
                }
                break;
            }
        }
    }
    selectedLocale = selectedLocale || window.PRELOADED_STATE.globalSettings.defaultLanguage;
    let dir = 'ltr';
    if (rtlDetect.isRtlLang(selectedLocale)) {
        dir = 'rtl';
    }
    
    return {
        locale: selectedLocale,
        dir
    };
})();

const getMessagesForLocale = (locale) => {
    const initial = initialLocale.locale;

    return {
        // Default any messages from the initial locale.
        ...window.PRELOADED_STATE.globalSettings.settingMessages.languages[initial].messages,
        ...(messages[initial] || messages[initial.split('-')?.[0]]),
        ...window.PRELOADED_STATE.globalSettings.settingMessages.languages[locale].messages,
        ...(messages[locale] || messages[locale.split('-')?.[0]])
    };
};

initialLocale.messages = getMessagesForLocale(initialLocale.locale);

const App = () => {
    const api = useApi();
    const [isLanguageLoading, setIsLanguageLoading] = useState(true);
    const [eteTheme, setEteTheme] = useState(window.PRELOADED_STATE?.templateTheme || window.PRELOADED_STATE?.globalTheme);
    const [theme, setTheme] = useState(parseTheme(eteTheme));
    const [globalTheme,] = useState(window.PRELOADED_STATE?.globalTheme);
    const [isAutosaving, setIsAutosaving] = useState(false);
    const [isThemeLoading, setIsThemeLoading] = useState(false);
    const rootCtx = {
        eteTheme,
        globalTheme,
        theme,
        isAutosaving,
        isThemeLoading,
        setIsThemeLoading,
        setTheme: (theme, eteTheme = {}) => {
            setTheme(theme);
            setEteTheme(eteTheme);
        },
        clearTheme: () => {
            if (globalTheme) {
                setTheme(parseTheme(globalTheme));
                setEteTheme(globalTheme);
            }
            else {
                setTheme(baseTheme);
                setEteTheme(null);
            }
        },
        setIsAutosaving
    };
    const [globalLocale, setGlobalLocale] = useState(initialLocale.locale);
    const [displayLocale, setDisplayLocale] = useState(''); // App content sets this.
    const [localeDirection, setLocaleDirection] = useState(initialLocale.dir);
    const [localizedMessages, setLocalizedMessages] = useState(initialLocale.messages);
    
    // Inject global CSS to prevent autofill blue background
    useEffect(() => {
        const styleId = 'autofill-override';
        if (!document.getElementById(styleId)) {
            const style = document.createElement('style');
            style.id = styleId;
            style.textContent = `
                input:-webkit-autofill,
                input:-webkit-autofill:hover,
                input:-webkit-autofill:focus,
                input:-webkit-autofill:active {
                    -webkit-box-shadow: 0 0 0 30px white inset !important;
                    box-shadow: 0 0 0 30px white inset !important;
                    -webkit-text-fill-color: inherit !important;
                    caret-color: inherit !important;
                    transition: background-color 5000s ease-in-out 0s;
                }
            `;
            document.head.appendChild(style);
        }
    }, []);
    
    // Check session for saved language preference
    useEffect(() => {
        (async () => {
            try {
                const data = await api.preferences.getLanguagePreference();
                if (data.locale && data.locale !== globalLocale) {
                    const settingMessages = window.PRELOADED_STATE.globalSettings.settingMessages;
                    const locales = Object.keys(settingMessages.languages);
                    const disabledLanguages = window.PRELOADED_STATE.globalSettings.disabledLanguages || [];
                    if (locales.includes(data.locale) && !disabledLanguages.includes(data.locale)) {
                        setGlobalLocale(data.locale);
                        setLocalizedMessages(getMessagesForLocale(data.locale));
                        setLocaleDirection(rtlDetect.isRtlLang(data.locale) ? 'rtl' : 'ltr');
                    }
                }
            } catch (err) {
                console.error(err);
            }
            finally {
                setIsLanguageLoading(false);
            }
        })();
    }, []);
    const memoizedAppContent = useMemo(() => (
        <SnackbarProvider>
            <AppContent />
        </SnackbarProvider>
    ), []);
    if (isLanguageLoading) {
        return null;
    }
    const globalLocaleCtx = {
        globalLocale,
        displayLocale,
        localeDirection,
        updateGlobalLocale: async (locale) => {
            // Update state immediately for better UX
            setGlobalLocale(locale);
            const newLocalizedMessages = getMessagesForLocale(locale);
            setLocalizedMessages(newLocalizedMessages);
            let dir = 'ltr';
            if (rtlDetect.isRtlLang(locale)) {
                dir = 'rtl';
            }
            if (localeDirection !== dir) {
                setLocaleDirection(dir);
            }
            
            // Persist to backend asynchronously
            try {
                await api.preferences.setLanguagePreference(locale);
            } catch (err) {
                console.error(err);
            }
        },
        updateDisplayLocale: (display) => setDisplayLocale(display),
    };
    return (
        <RootContext.Provider value={rootCtx}>
            <div dir={localeDirection}>
                <GlobalLocaleContext.Provider value={globalLocaleCtx}>
                    <IntlProvider locale={globalLocale || navigator.language} messages={localizedMessages}>
                        <EDSContent
                            theme={theme}
                            globalLocale={globalLocale}
                            localeDirection={localeDirection}
                        >
                            {memoizedAppContent}
                        </EDSContent>
                    </IntlProvider>
                </GlobalLocaleContext.Provider>
            </div>
        </RootContext.Provider>
    );
};

export default App;